import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';
import { DrilldownBanner } from '@/components/dashboard/drilldown-banner';
import type { TaskInboxSearchRequest, TaskWithRelations } from '@vagusx/clients';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import {
  useTask,
  useTaskInbox,
  useTaskCounts,
  useMarkTaskRead,
  useStarTask,
  useUnstarTask,
} from '@/hooks/use-tasks';
import { useClassificationLabelMap } from '@/hooks/use-classification-label';
import { useEscapeKey } from '@/hooks/use-escape-key';
import type { FilterState } from '@/components/filter-bar/filter-bar';
import {
  urlToFilterState,
  filterStateToUrl,
  filterStateToApiParams,
  urlToScope,
  type TaskScope,
} from './task-filter-utils';
import { useTaskFilterDefinitions } from './TaskFilterBar';
import { usePersistentInboxFilters } from './use-persistent-inbox-filters';
import { TaskListHeader } from './TaskListHeader';
import { TaskListPanel } from './TaskListPanel';
import { TaskWorkPanel } from './TaskWorkPanel';
import { useTaskBulkActions } from './use-task-bulk-actions';

const PAGE_SIZE = 25;

export default function TasksInboxPage(): React.ReactElement {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { taskId?: string };
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const markRead = useMarkTaskRead();
  const starTask = useStarTask();
  const unstarTask = useUnstarTask();
  const labelMap = useClassificationLabelMap();
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  // Selected task from URL
  const selectedTaskId = params.taskId;

  // Drilldown mode: when arriving from the dashboard, the URL is
  // authoritative for this view and we deliberately DON'T mutate the
  // user's saved tasks-inbox preference. The persistent-filter hook
  // becomes inert; the drilldown banner renders at the top.
  const fromDashboard = search.from === 'dashboard';

  // Cross-session filter persistence: hydrate from server once on bare-URL
  // mount (redirect-once), debounced save on filter changes. URL stays
  // the in-session source of truth; the hook bridges it with the
  // user_preferences row at the session edges. Disabled in drilldown
  // mode so a transient drilldown view doesn't overwrite saved prefs.
  const { waitingForHydrate } = usePersistentInboxFilters({
    search,
    selectedTaskId,
    navigate,
    enabled: !fromDashboard,
  });

  // Search from URL
  const searchFromUrl = search.search;

  // Local state
  const [searchTerm, setSearchTerm] = useState(searchFromUrl ?? '');
  const [page, setPage] = useState(1);
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Filter definitions (includes dynamic labels)
  const filterDefinitions = useTaskFilterDefinitions();

  // Extract individual primitive values from search for stable useMemo deps.
  // `useSearch()` returns a new object reference on every render.
  const urlStatus = search.status;
  const urlShow = search.show;
  const urlLabels = search.labels;
  const urlDateFrom = search.dateFrom;
  const urlDateTo = search.dateTo;

  // Derive filter state from URL
  const filterState = useMemo(
    () => urlToFilterState(search),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- individual primitives for stable reference
    [urlStatus, urlShow, urlLabels, urlDateFrom, urlDateTo]
  );

  // Optional URL-pinned `assignedTo` (+ `assigneeType` disambiguator).
  // Set by the dashboard's "tasks by assignee" drilldown AND by the
  // explicit Assignee filter chip on the All tab. The route's
  // validateSearch enum-validates `assigneeType`, so by the time we
  // read it here it's guaranteed to be 'user' | 'team' | 'agent' or
  // absent — the loose `string` indexing on `search` (because we use
  // `strict: false` to share the search object with non-typed code
  // paths) means we still narrow defensively.
  const urlAssignedTo = search.assignedTo;
  const urlAssigneeType: 'user' | 'team' | 'agent' | undefined =
    search.assigneeType === 'user' ||
    search.assigneeType === 'team' ||
    search.assigneeType === 'agent'
      ? search.assigneeType
      : undefined;

  const inboxParams = useMemo((): TaskInboxSearchRequest => {
    const searchParams: Record<string, string | undefined> = {
      status: urlStatus,
      show: urlShow,
      labels: urlLabels,
      dateFrom: urlDateFrom,
      dateTo: urlDateTo,
    };
    const apiFilters = filterStateToApiParams(searchParams);
    return {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      searchTerm: searchTerm || undefined,
      ...apiFilters,
      ...(urlAssignedTo ? { assignedTo: urlAssignedTo } : {}),
      ...(urlAssignedTo && urlAssigneeType ? { assigneeType: urlAssigneeType } : {}),
    };
  }, [
    page,
    urlStatus,
    urlShow,
    urlLabels,
    urlDateFrom,
    urlDateTo,
    searchTerm,
    urlAssignedTo,
    urlAssigneeType,
  ]);

  // `mine` is resolved server-side from the session — no need to gate on a
  // client-side userId fetch. Gate the query while we're still waiting
  // for a bare-URL hydrate so we don't fire with URL-defaults just to
  // refetch moments later after the redirect lands.
  const { data, isLoading, isFetching, refetch } = useTaskInbox(inboxParams, {
    enabled: !waitingForHydrate,
  });

  // ── Scope tabs + per-tab counts ─────────────────────────────────────
  // Counts honor the current status + date filters so the numbers match
  // what the user is actually viewing.
  const scope: TaskScope = useMemo(
    () => urlToScope(search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [urlStatus, urlShow, urlLabels, urlDateFrom, urlDateTo]
  );
  const countsRequest = useMemo(
    () => ({
      status: inboxParams.status,
      dateFrom: inboxParams.dateFrom,
      dateTo: inboxParams.dateTo,
      ...(inboxParams.assignedTo ? { assignedTo: inboxParams.assignedTo } : {}),
      ...(inboxParams.assignedTo && inboxParams.assigneeType
        ? { assigneeType: inboxParams.assigneeType }
        : {}),
      // Forward labels too — without this, dashboard "tasks by label"
      // drilldowns leave the tab counts at org-wide totals while the
      // visible list IS correctly filtered, leading the user to think
      // nothing happened.
      ...(inboxParams.labels && inboxParams.labels.length > 0
        ? { labels: inboxParams.labels }
        : {}),
    }),
    [
      inboxParams.status,
      inboxParams.dateFrom,
      inboxParams.dateTo,
      inboxParams.assignedTo,
      inboxParams.assigneeType,
      inboxParams.labels,
    ]
  );
  const { data: counts } = useTaskCounts(countsRequest, { enabled: !waitingForHydrate });
  const scopeCounts = useMemo(
    () => ({
      my: counts?.scopes.mine ?? 0,
      all: counts?.scopes.all ?? 0,
      unassigned: counts?.scopes.unassigned ?? 0,
    }),
    [counts]
  );

  const handleScopeChange = useCallback(
    (next: TaskScope) => {
      setPage(1);
      // Update only the `show` URL param — leave status/labels/date alone.
      const current = (search ?? {}) as Record<string, string | undefined>;
      const newSearch: Record<string, string> = {};
      for (const [k, v] of Object.entries(current)) {
        if (v !== undefined && v !== '' && k !== 'show') newSearch[k] = v;
      }
      newSearch.show = next;
      if (selectedTaskId) {
        navigate({
          to: '/tasks/$taskId',
          params: { taskId: selectedTaskId },
          search: newSearch,
          replace: true,
        });
      } else {
        navigate({ to: '/tasks', search: newSearch, replace: true });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, selectedTaskId, search]
  );

  const tasks = data?.items ?? [];
  const total = data?.total ?? 0;

  // Bulk-actions state lives here (not in TaskListPanel) so the
  // toolbar checkbox in TaskListHeader can drive forced selection
  // mode AND the panel can render checkboxes / the action bar from
  // the same hook instance.
  const idsOnPage = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const bulk = useTaskBulkActions(idsOnPage);

  // Fallback fetch when the selected task isn't on the current page
  const taskNotInList = !!selectedTaskId && !tasks.some((t) => t.id === selectedTaskId);
  const { data: fetchedTask } = useTask(taskNotInList ? selectedTaskId : undefined);

  // Find the full task object for the selected task
  const selectedTask = useMemo(() => {
    const fromList = tasks.find((t) => t.id === selectedTaskId);
    if (fromList) return fromList;
    if (fetchedTask) {
      // `labels` is a real column returned by the by-id endpoint, so carry it
      // through — it gates the Overview action drawers (schedule-appointment)
      // and the classification badges. Only patientName/assignedToName are
      // list-only join enrichments the by-id endpoint doesn't compute.
      return {
        ...fetchedTask,
        patientName: null,
        assignedToName: null,
      } as TaskWithRelations;
    }
    return undefined;
  }, [tasks, selectedTaskId, fetchedTask]);

  // Auto mark-read with debounce
  useEffect(() => {
    if (markReadTimerRef.current) {
      clearTimeout(markReadTimerRef.current);
    }

    if (selectedTask && !selectedTask.readAt) {
      markReadTimerRef.current = setTimeout(() => {
        markReadRef.current.mutate(selectedTask.id);
      }, 500);
    }

    return () => {
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
      }
    };
  }, [selectedTask?.id]);

  // Navigate to task
  const handleSelectTask = useCallback(
    (id: string) => {
      navigate({
        to: '/tasks/$taskId',
        params: { taskId: id },
        search: search as Record<string, string>,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Update URL search params
  const updateSearch = useCallback(
    (updates: Record<string, string | undefined>) => {
      const current = (search ?? {}) as Record<string, string | undefined>;
      const newSearch: Record<string, string> = {};
      for (const [key, value] of Object.entries({ ...current, ...updates })) {
        if (value !== undefined && value !== '') {
          newSearch[key] = value;
        }
      }

      if (selectedTaskId) {
        navigate({
          to: '/tasks/$taskId',
          params: { taskId: selectedTaskId },
          search: newSearch,
          replace: true,
        });
      } else {
        navigate({
          to: '/tasks',
          search: newSearch,
          replace: true,
        });
      }
    },
    [navigate, search, selectedTaskId]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setPage(1);
      updateSearch({ search: value || undefined });
    },
    [updateSearch]
  );

  // Handle filter changes from FilterBar
  const handleFilterChange = useCallback(
    (newState: FilterState) => {
      setPage(1);
      // Convert FilterState to URL params, clearing all filter-related params first
      const urlParams = filterStateToUrl(newState);
      // Build new search: keep non-filter params (search), replace filter params
      const current = (search ?? {}) as Record<string, string | undefined>;
      const newSearch: Record<string, string> = {};

      // Keep non-filter params (e.g. search)
      if (current.search) newSearch.search = current.search;

      // Add filter params
      for (const [key, value] of Object.entries(urlParams)) {
        if (value !== undefined && value !== '') {
          newSearch[key] = value;
        }
      }

      if (selectedTaskId) {
        navigate({
          to: '/tasks/$taskId',
          params: { taskId: selectedTaskId },
          search: newSearch,
          replace: true,
        });
      } else {
        navigate({
          to: '/tasks',
          search: newSearch,
          replace: true,
        });
      }
    },
    [navigate, search, selectedTaskId]
  );

  // Toggle star on a task
  const handleToggleStar = useCallback(
    (id: string, isCurrentlyStarred: boolean) => {
      if (isCurrentlyStarred) {
        unstarTask.mutate(id);
      } else {
        starTask.mutate(id);
      }
    },
    [starTask, unstarTask]
  );

  // Escape key: deselect task (useEscapeKey respects open Radix dialogs)
  useEscapeKey(
    useCallback(() => {
      navigate({
        to: '/tasks',
        search: search as Record<string, string>,
        replace: true,
      });
    }, [navigate, search])
  );

  // j/k and arrow key navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        const currentIndex = tasks.findIndex((t) => t.id === selectedTaskId);
        const nextIndex = currentIndex + 1;
        if (nextIndex < tasks.length) {
          const nextId = tasks[nextIndex]!.id;
          handleSelectTask(nextId);
          document
            .querySelector<HTMLElement>(`[data-task-id="${nextId}"]`)
            ?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIndex = tasks.findIndex((t) => t.id === selectedTaskId);
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          const prevId = tasks[prevIndex]!.id;
          handleSelectTask(prevId);
          document
            .querySelector<HTMLElement>(`[data-task-id="${prevId}"]`)
            ?.scrollIntoView({ block: 'nearest' });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tasks, selectedTaskId, handleSelectTask]);

  return (
    // `h-full min-h-0` instead of `h-[calc(100vh-0px)]`: the latter
    // ignored the impersonation banner above the route shell and pushed
    // the bottom of this container 49px below the viewport, clipping
    // the pagination footer in the left task list. With `h-full min-h-0`
    // the page absorbs whatever height its layout parent (the
    // authenticated-route container) gives it — bounded by the actual
    // remaining viewport, not 100vh.
    <div className="flex h-full min-h-0 flex-col">
      <DrilldownBanner />
      <ResizablePanelGroup orientation="horizontal" className="flex-1">
        {/* Left panel: task list */}
        <ResizablePanel defaultSize="22" minSize="15" maxSize="30">
          <div className="flex h-full flex-col">
            <TaskListHeader
              scope={scope}
              scopeCounts={scopeCounts}
              onScopeChange={handleScopeChange}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              filterState={filterState}
              onFilterChange={handleFilterChange}
              filterDefinitions={filterDefinitions}
              onRefresh={() => refetch()}
              isFetching={isFetching}
              selectedCount={bulk.selection.count}
              idsOnPageCount={bulk.idsOnPageCount}
              allOnPageSelected={bulk.allOnPageSelected}
              onToggleSelectAllOnPage={bulk.onToggleSelectAllOnPage}
            />
            <div className="min-h-0 flex-1">
              <TaskListPanel
                tasks={tasks}
                total={total}
                isLoading={isLoading}
                selectedTaskId={selectedTaskId}
                onSelectTask={handleSelectTask}
                onToggleStar={handleToggleStar}
                labelMap={labelMap}
                page={page}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                bulk={bulk}
              />
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right panel: task detail */}
        <ResizablePanel defaultSize="78" minSize="50">
          {selectedTask ? (
            <TaskWorkPanel key={selectedTask.id} task={selectedTask} labelMap={labelMap} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
              <Inbox className="h-12 w-12" />
              <p className="text-sm">Select a task to view details</p>
              <p className="text-xs">Use j/k or arrow keys to navigate</p>
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
